import React, { Component } from 'react';
import { Link } from 'react-router-dom';
import Breadcrumbs from '../../components/common/Breadcrumbs';
import {
  listNotifications,
  markRead,
  markReadAll,
  dismissNotification,
  dismissReadNotifications,
  archiveNotification,
  getPreferences,
  putPreferences,
} from '../../platform/notifications/notificationApi';
import '../../platform/notifications/notifications.css';

class NotificationCenterPage extends Component {
  state = {
    items: [],
    loading: true,
    filter: 'all',
    prefs: [],
    showPrefs: false,
    dismissingAll: false,
  };

  async componentDidMount() {
    await this.load();
  }

  load = async () => {
    this.setState({ loading: true });
    try {
      const items = await listNotifications({
        unread: this.state.filter === 'unread' ? '1' : undefined,
        limit: 100,
      });
      this.setState({ items, loading: false });
    } catch (_) {
      this.setState({ loading: false });
    }
  };

  setFilter = async (filter) => {
    this.setState({ filter }, this.load);
  };

  dismissRead = async () => {
    if (this.state.dismissingAll || !this.state.items.some((n) => n.readAt)) return;
    this.setState({ dismissingAll: true });
    try {
      await dismissReadNotifications();
      await this.load();
    } finally {
      this.setState({ dismissingAll: false });
    }
  };

  openPrefs = async () => {
    const prefs = await getPreferences();
    this.setState({
      showPrefs: true,
      prefs: prefs.length
        ? prefs
        : [
            { eventPattern: 'ticket.*', channel: 'in_app', enabled: true, digest: 'none' },
            { eventPattern: 'invoice.*', channel: 'in_app', enabled: true, digest: 'none' },
            { eventPattern: 'payment.*', channel: 'in_app', enabled: true, digest: 'none' },
          ],
    });
  };

  savePrefs = async () => {
    await putPreferences(this.state.prefs);
    this.setState({ showPrefs: false });
  };

  render() {
    const { items, loading, filter, showPrefs, prefs, dismissingAll } = this.state;
    return (
      <div className="py-3 notification-center">
        <Breadcrumbs
          items={[
            { label: 'Home', to: '/home' },
            { label: 'Notifications' },
          ]}
        />
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h4 className="mb-0">Notification Center</h4>
          <div>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mr-2"
              onClick={this.openPrefs}
            >
              Preferences
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-secondary mr-2"
              disabled={dismissingAll || loading || !items.some((n) => n.readAt)}
              onClick={this.dismissRead}
            >
              {dismissingAll ? 'Dismissing…' : 'Dismiss read'}
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-primary"
              onClick={async () => {
                await markReadAll();
                this.load();
              }}
            >
              Mark all read
            </button>
          </div>
        </div>

        <div className="btn-group btn-group-sm mb-3">
          {['all', 'unread'].map((f) => (
            <button
              key={f}
              type="button"
              className={`btn btn-outline-secondary${filter === f ? ' active' : ''}`}
              onClick={() => this.setFilter(f)}
            >
              {f === 'all' ? 'All' : 'Unread'}
            </button>
          ))}
        </div>

        {showPrefs && (
          <div className="card mb-3">
            <div className="card-body">
              <h6>Notification preferences</h6>
              <p className="small notification-center__meta">
                Patterns like <code>ticket.*</code> or <code>invoice.*</code>.
              </p>
              {prefs.map((p, i) => (
                <div key={i} className="form-row align-items-center mb-2">
                  <div className="col-md-4">
                    <input
                      className="form-control form-control-sm"
                      value={p.eventPattern}
                      onChange={(e) => {
                        const next = [...prefs];
                        next[i] = { ...p, eventPattern: e.target.value };
                        this.setState({ prefs: next });
                      }}
                    />
                  </div>
                  <div className="col-md-2">
                    <select
                      className="form-control form-control-sm"
                      value={p.channel}
                      onChange={(e) => {
                        const next = [...prefs];
                        next[i] = { ...p, channel: e.target.value };
                        this.setState({ prefs: next });
                      }}
                    >
                      <option value="in_app">In-app</option>
                      <option value="email">Email</option>
                    </select>
                  </div>
                  <div className="col-md-2">
                    <label className="small mb-0">
                      <input
                        type="checkbox"
                        checked={p.enabled !== false}
                        onChange={(e) => {
                          const next = [...prefs];
                          next[i] = { ...p, enabled: e.target.checked };
                          this.setState({ prefs: next });
                        }}
                      />{' '}
                      Enabled
                    </label>
                  </div>
                </div>
              ))}
              <button type="button" className="btn btn-sm btn-primary mr-2" onClick={this.savePrefs}>
                Save
              </button>
              <button
                type="button"
                className="btn btn-sm btn-secondary"
                onClick={() => this.setState({ showPrefs: false })}
              >
                Cancel
              </button>
            </div>
          </div>
        )}

        {loading && <p className="notification-center__meta">Loading…</p>}
        {!loading && !items.length && (
          <p className="notification-center__meta">No notifications.</p>
        )}
        <div className="list-group">
          {items.map((n) => (
            <div
              key={n.id}
              className={`list-group-item notification-center__item${
                n.readAt ? '' : ' notification-center__item--unread'
              }`}
            >
              <div className="d-flex justify-content-between">
                <div>
                  <Link
                    to={n.url || '#'}
                    className={`notification-center__title${
                      n.readAt ? ' notification-center__title--read' : ' font-weight-bold'
                    }`}
                    onClick={() => markRead([n.id]).then(this.load)}
                  >
                    {n.title}
                  </Link>
                  {n.body && <div className="small notification-center__body">{n.body}</div>}
                  <div className="small notification-center__meta">
                    {n.category || n.type}
                    {n.priority ? ` · ${n.priority}` : ''}
                    {n.createdAt ? ` · ${new Date(n.createdAt).toLocaleString()}` : ''}
                  </div>
                </div>
                <div className="text-right">
                  <button
                    type="button"
                    className="btn btn-sm btn-link"
                    onClick={() => dismissNotification(n.id).then(this.load)}
                  >
                    Dismiss
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-link"
                    onClick={() => archiveNotification(n.id).then(this.load)}
                  >
                    Archive
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }
}

export default NotificationCenterPage;
